import { usePurchaseMainCard } from "@/cards/common/api/taascard.api";
import SaveCard from "@/cards/common/component/SaveCard";
import type { DeckCardThumbnailContent } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import { formatDeckCardTypeLabel } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import { invalidateDeckMainCardByIdQueries } from "@/cards/common/utils/deckMainCardByIdQuery";
import useSealTimeGate, {
  parseSealDeadline,
  type SealTimeInput,
} from "@/cards/common/utils/useSealTimeGate";
import { useRemoveCardFromCharmCards } from "@/modules/charm/api/charm-cards.api";
import type { DeckCardKakaoKind } from "@/modules/kakao/deckCard/deckCardKakaoKind";
import { TAAS_SANS } from "@/shared/branding/taasBrand";
import { cardBackScrollSx, GOLD, GOLD_DARK, GOLD_LIGHT } from "@/shared/constants/cardLayout";
import { useAuth } from "@/shared/stores/useAuth";
import CheckIcon from "@mui/icons-material/Check";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ImpactCardFundsReport from "@/cards/impact-card/components/ImpactCardFundsReport";
import { isImpactCardType } from "@/cards/impact-card/utils/impactCardType";
import type { ImpactUseOfFund } from "@/cards/impact-card/utils/impactCardData";

type DeckCardExpandDetailsDrawerProps = {
  content?: DeckCardThumbnailContent | null;
  cardType?: string;
  cardTitle?: string;
  talentName?: string;
  /** Optional avatar image for the talent shown in the header row. */
  talentAvatarUrl?: string;
  price?: number;
  mainCardId?: number;
  /** Content / product id used for PortOne (content.id or ref_id). */
  productId?: number;
  deckStatus?: boolean;
  sealTime?: SealTimeInput;
  cardStatus?: string;
  isPurchased?: boolean;
  isOwner?: boolean;
  isLoggedIn?: boolean;
  shareKind?: DeckCardKakaoKind;
  /** Number of content pieces added to this card so far, shown in the "Content Added" section. */
  pieceCount?: number;
  purchase?: { id?: number; created_at?: string } | null;
  onOpenDetails: () => void;
  onRequireLogin?: () => void;
  /** Parent-provided API action for free Impact Card collection. */
  onCollectImpact?: () => Promise<void> | void;
  isCollectingImpact?: boolean;
  talentId?: number;
  impactFunds?: {
    impactGoal?: string;
    expectedOutcome?: string;
    targetAmount?: number;
    committedFund?: number;
    verifiedFund?: number;
    contributorCount?: number;
    collectorCount?: number;
    clickedCount?: number;
    viewedCount?: number;
    useOfFunds?: ImpactUseOfFund[];
    beneficiaryName?: string;
  } | null;
};

/** Drawer text palette — keep hierarchy consistent across all sections. */
const TEXT = {
  /** Main titles, body copy, dates, button labels */
  primary: "#f8f4ea",
  /** Numeric stats (chip count, piece count) */
  stat: GOLD,
  /** Section labels, metadata prefixes ("Seals on", "Collected") */
  muted: "rgba(232, 213, 163, 0.72)",
  /** Chip / badge labels (SEASON, Saved to Charm) */
  label: GOLD_LIGHT,
} as const;

const sectionLabelSx = {
  fontSize: "0.58rem",
  letterSpacing: "0.12em",
  color: TEXT.muted,
  textTransform: "uppercase" as const,
  mb: 0.85,
};

const surfaceSx = {
  p: { xs: 1.25, sm: 1.5 },
  borderRadius: "12px",
  bgcolor: "rgba(200, 164, 75, 0.06)",
  border: `1px solid ${GOLD_DARK}`,
  minWidth: 0,
  width: "100%",
  boxSizing: "border-box" as const,
};

const typeChipSx = {
  height: 22,
  fontSize: "0.62rem",
  fontWeight: 700,
  minWidth: 60,
  p: 1,
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  bgcolor: "rgba(200,164,75,0.1)",
  color: TEXT.label,
  border: `1px solid ${GOLD_DARK}`,
  flexShrink: 0,
  maxWidth: "100%",
};

const statusPillBaseSx = {
  height: 24,
  fontSize: "0.62rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  textTransform: "uppercase" as const,
  "& .MuiChip-icon": {
    fontSize: "0.9rem",
    ml: "6px",
  },
  maxWidth: "100%",
};

export const deckCardDrawerScrollSx = {
  ...cardBackScrollSx,
  px: 0,
  pb: { xs: 2, sm: 2.5 },
  maxWidth: 640,
  mx: "auto",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  overflowX: "hidden",
};

const actionBtnSx = {
  textTransform: "none" as const,
  fontSize: { xs: "0.68rem", sm: "0.72rem" },
  fontWeight: 700,
  letterSpacing: "0.08em",
  py: { xs: 0.75, sm: 0.85 },
  px: { xs: 1.35, sm: 1.75 },
  minWidth: 0,
  borderRadius: "10px",
  bgcolor: "rgba(200, 164, 75, 0.92)",
  color: "#1a1410",
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: "rgba(232, 213, 163, 0.95)",
  },
};

const secondaryBtnSx = {
  textTransform: "none" as const,
  fontSize: { xs: "0.68rem", sm: "0.72rem" },
  fontWeight: 700,
  letterSpacing: "0.06em",
  py: { xs: 0.7, sm: 0.75 },
  px: { xs: 1.2, sm: 1.5 },
  minWidth: 0,
  borderRadius: "10px",
  color: TEXT.primary,
  borderColor: GOLD_DARK,
  bgcolor: "rgba(200, 164, 75, 0.08)",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": {
    color: TEXT.primary,
    marginRight: { xs: 0.4, sm: 0.75 },
  },
  "&:hover": {
    borderColor: GOLD,
    bgcolor: "rgba(200, 164, 75, 0.16)",
    color: TEXT.primary,
  },
};

type CardDetailsKeys = {
  labelKey: string;
  unlockTitleKey: string;
  unlockSubtitleKey: string;
  confirmTitleKey?: string;
  confirmBodyKey?: string;
  successMessageKey?: string;
};

const CARD_DETAIL_KEYS: Record<DeckCardKakaoKind, CardDetailsKeys> = {
  single: {
    labelKey: "deck_single_label",
    unlockTitleKey: "deck_single_unlock_title",
    unlockSubtitleKey: "deck_single_unlock_subtitle",
    confirmTitleKey: "deck_single_confirm_title",
    confirmBodyKey: "deck_single_confirm_body",
    successMessageKey: "deck_single_success",
  },

  demo: {
    labelKey: "deck_demo_label",
    unlockTitleKey: "deck_demo_unlock_title",
    unlockSubtitleKey: "deck_demo_unlock_subtitle",
    confirmTitleKey: "deck_demo_confirm_title",
    confirmBodyKey: "deck_demo_confirm_body",
    successMessageKey: "deck_demo_success",
  },

  season: {
    labelKey: "deck_season_label",
    unlockTitleKey: "deck_season_unlock_title",
    unlockSubtitleKey: "deck_season_unlock_subtitle",
    confirmTitleKey: "deck_season_confirm_title",
    confirmBodyKey: "deck_season_confirm_body",
    successMessageKey: "deck_season_success",
  },

  collection: {
    labelKey: "deck_collection_label",
    unlockTitleKey: "deck_collection_unlock_title",
    unlockSubtitleKey: "deck_collection_unlock_subtitle",
  },

  impact: {
    labelKey: "deck_impact_label",
    unlockTitleKey: "deck_impact_unlock_title",
    unlockSubtitleKey: "deck_impact_unlock_subtitle",
  },
};

type ResolvedCardDetails = {
  label: string;
  unlockTitle: string;
  unlockSubtitle: string;
  confirmTitle: string;
  confirmBody: (price: number) => string;
  successMessage: string;
};

const resolveCardDetails = (
  t: (key: string, options?: Record<string, unknown>) => string,
  kind: DeckCardKakaoKind
): ResolvedCardDetails => {
  const keys = CARD_DETAIL_KEYS[kind] ?? CARD_DETAIL_KEYS.single;

  return {
    label: t(keys.labelKey),
    unlockTitle: t(keys.unlockTitleKey),
    unlockSubtitle: t(keys.unlockSubtitleKey),
    confirmTitle: keys.confirmTitleKey ? t(keys.confirmTitleKey) : "",
    confirmBody: (price) => (keys.confirmBodyKey ? t(keys.confirmBodyKey, { price }) : ""),
    successMessage: keys.successMessageKey ? t(keys.successMessageKey) : "",
  };
};

/** Format a short display date, e.g. "Sep 1, 2026". */
const formatShortDate = (value?: string | number | Date | null): string => {
  if (value == null) return "";

  const d = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(d.getTime())) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
};

/** Purchased when API `purchase` has a valid `id` (otherwise `purchase` is null). */
const hasPurchaseId = (purchase?: { id?: number } | null): boolean => {
  const id = Number(purchase?.id);

  return Number.isFinite(id) && id > 0;
};

const getPurchaseCreatedAt = (
  purchase?: { id?: number; created_at?: string } | null
): string | null => {
  if (!hasPurchaseId(purchase)) return null;

  const createdAt = purchase?.created_at;

  return typeof createdAt === "string" && createdAt.trim() ? createdAt : null;
};

const refetchDeckCardExpandQueries = (queryClient: QueryClient, mainCardId: number) => {
  if (!Number.isFinite(mainCardId) || mainCardId <= 0) return;

  void queryClient.refetchQueries({
    queryKey: ["main-card-by-id", mainCardId],
  });

  invalidateDeckMainCardByIdQueries(queryClient, mainCardId);

  void queryClient.invalidateQueries({
    queryKey: ["talent-deck-main-cards", mainCardId],
  });

  void queryClient.invalidateQueries({
    queryKey: ["card-thumbnail-by-id", mainCardId],
  });
};

const DeckCardExpandDetailsDrawer: React.FC<DeckCardExpandDetailsDrawerProps> = ({
  content,
  cardType,
  cardTitle,
  talentName,
  talentAvatarUrl,
  price,
  mainCardId = 0,
  deckStatus,
  sealTime,
  cardStatus,
  isPurchased = false,
  isOwner = false,
  isLoggedIn = false,
  shareKind = "single",
  pieceCount,
  purchase,
  onOpenDetails,
  onRequireLogin,
  onCollectImpact,
  talentId,
  impactFunds,

}) => {
  const { t } = useTranslation("taascard");
  const queryClient = useQueryClient();
  const { userInfo } = useAuth();

  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [localPurchased, setLocalPurchased] = useState(false);
  const [isCollectingImpact, setIsCollectingImpact] = useState(false);
  const [localPurchasedAt, setLocalPurchasedAt] = useState<string | number | Date | null>(null);
  const [localDeckStatus, setLocalDeckStatus] = useState<boolean | null>(null);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);

  const [localSealTime, setLocalSealTime] = useState<SealTimeInput>(() =>
    parseSealDeadline(sealTime) != null ? sealTime : null
  );

  const { countdownLabel, hasSealTime, isSealing, isSealed } = useSealTimeGate(localSealTime);

  useEffect(() => {
    setLocalPurchased(false);
    setIsCollectingImpact(false);
    setLocalPurchasedAt(null);
    setLocalDeckStatus(null);
    setPurchaseOpen(false);
    setOpenRemoveDialog(false);
    setSelectedCardId(null);
  }, [mainCardId]);

  useEffect(() => {
    setLocalSealTime(parseSealDeadline(sealTime) != null ? sealTime : null);
  }, [sealTime]);

  const title = content?.title || cardTitle;

  const details = useMemo(() => resolveCardDetails(t, shareKind), [t, shareKind]);

  const typeLabel =
    formatDeckCardTypeLabel(cardType) ||
    t(CARD_DETAIL_KEYS[shareKind]?.labelKey ?? CARD_DETAIL_KEYS.single.labelKey);

  const chipPrice = Number(
    price ?? content?.price ?? (content as { chip_price?: number })?.chip_price ?? 0
  );

  const purchased = hasPurchaseId(purchase) || isPurchased || localPurchased;

  const isCollection = shareKind === "collection" || cardType === "Collection_Card";

  const isImpactCard = isImpactCardType(cardType);

  const canBuy = !isImpactCard && !isCollection && !isOwner && !purchased;
  const canCollectImpact = isImpactCard && !isOwner && !purchased;

  const sealDateLabel = formatShortDate(localSealTime ?? sealTime);

  const purchasedDateLabel = formatShortDate(localPurchasedAt ?? getPurchaseCreatedAt(purchase));

  const alreadySaved = localDeckStatus ?? Boolean(deckStatus);

  const { update: removeCardFromCharmCards } = useRemoveCardFromCharmCards({
    key: ["remove-card-from-charm-deck", userInfo?.id, selectedCardId],
    params: {
      main_card_id: selectedCardId ?? 0,
    },
  });

  // Lifecycle chip: LIVING while the card can still receive content,
  // SEALED once locked, falling back to the raw cardStatus label
  // when no seal info is available at all.
  const lifecycleChip =
    hasSealTime && isSealed
      ? {
          label: t("deck_sealed_status"),
          color: GOLD_LIGHT,
          bg: "var(--warningBorderAlpha)",
          border: "var(--warningBorder)",
        }
      : {
          label: t("deck_living_status"),
          color: GOLD_LIGHT,
          bg: "var(--secondaryBorderAlpha)",
          border: "var(--secondaryBorder)",
        };

  const { update: purchaseMainCard, isLoading: isPurchasing } = usePurchaseMainCard({
    key: ["purchase-main-card", "expand-drawer", mainCardId],
  });

  const handlePurchaseSuccess = (message?: string) => {
    setLocalPurchased(true);
    setLocalPurchasedAt(new Date());
    setPurchaseOpen(false);

    enqueueSnackbar(message || details.successMessage, {
      variant: "success",
    });

    refetchDeckCardExpandQueries(queryClient, mainCardId);
  };

  const handleBuyClick = () => {
    if (!isLoggedIn) {
      onRequireLogin?.();
      return;
    }

    if (!mainCardId || mainCardId <= 0) {
      enqueueSnackbar(t("deck_card_unavailable"), {
        variant: "error",
      });

      return;
    }

    setPurchaseOpen(true);
  };

  const handleCollectImpact = async () => {
    if (!isLoggedIn) {
      onRequireLogin?.();
      return;
    }

    if (!onCollectImpact || isCollectingImpact) return;

    setIsCollectingImpact(true);
    try {
      await onCollectImpact();
      setLocalPurchased(true);
      setLocalPurchasedAt(new Date());
      if (mainCardId > 0) refetchDeckCardExpandQueries(queryClient, mainCardId);
      enqueueSnackbar("Impact Card collected successfully", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : "Unable to collect Impact Card",
        { variant: "error" }
      );
    } finally {
      setIsCollectingImpact(false);
    }
  };

  const handleConfirmChipPurchase = async () => {
    if (!Number.isFinite(mainCardId) || mainCardId <= 0) {
      enqueueSnackbar(t("deck_card_unavailable"), {
        variant: "error",
      });

      return;
    }

    await purchaseMainCard(
      { main_card_id: mainCardId },
      {
        onSuccess: () => handlePurchaseSuccess(),

        onError: (err) => {
          const message =
            (err as any)?.response?.data?.message ?? err?.message ?? t("deck_purchase_failed");

          enqueueSnackbar(message, {
            variant: "error",
          });
        },
      }
    );
  };

  const handleOpenRemoveDialog = () => {
    if (!Number.isFinite(mainCardId) || mainCardId <= 0) return;

    setSelectedCardId(mainCardId);
    setOpenRemoveDialog(true);
  };

  const handleCloseRemoveDialog = () => {
    setOpenRemoveDialog(false);
    setSelectedCardId(null);
  };

  const handleRemoveCardFromCharmCards = async () => {
    if (selectedCardId == null) return;

    try {
      await removeCardFromCharmCards({
        main_card_id: selectedCardId,
      });

      setLocalDeckStatus(false);
      refetchDeckCardExpandQueries(queryClient, selectedCardId);
      void queryClient.invalidateQueries({ queryKey: ["card-charm-deck"] });
      void queryClient.invalidateQueries({ queryKey: ["card-saved-deck"] });

      enqueueSnackbar(t("card_removed_from_charm_successfully"), {
        variant: "success",
      });

      handleCloseRemoveDialog();
    } catch {
      enqueueSnackbar(t("error_removing_card_from_charm"), {
        variant: "error",
      });
    }
  };

  return (
    <Box sx={deckCardDrawerScrollSx} data-no-card-tap>
      <Stack
        spacing={{ xs: 1.5, sm: 2 }}
        sx={{
          minWidth: 0,
          width: "100%",
        }}
      >
        {/* Overview */}
        <Box>
          <Box sx={surfaceSx}>
            <Stack spacing={1.1}>
              {/* Talent header + lifecycle chips */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems={{
                  xs: "flex-start",
                  sm: "center",
                }}
                flexWrap="wrap"
                useFlexGap
                rowGap={0.75}
                columnGap={1}
                sx={{
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.9}
                  alignItems="center"
                  sx={{
                    minWidth: 0,
                    flex: "1 1 auto",
                  }}
                >
                  <Avatar
                    src={talentAvatarUrl}
                    sx={{
                      width: 26,
                      height: 26,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      bgcolor: "rgba(200,164,75,0.22)",
                      color: TEXT.label,
                      border: `1px solid ${GOLD_DARK}`,
                      flexShrink: 0,
                    }}
                  >
                    {talentName?.trim()?.[0]?.toUpperCase() ?? "?"}
                  </Avatar>

                  {talentName && (
                    <Typography
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: TEXT.primary,
                        minWidth: 0,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {talentName}
                    </Typography>
                  )}
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.6}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{
                    minWidth: 0,
                    flexShrink: 1,
                    maxWidth: "100%",
                  }}
                >
                  {typeLabel && <Chip label={typeLabel} size="small" sx={typeChipSx} />}

                  {lifecycleChip && (
                    <Chip
                      label={t(lifecycleChip.label)}
                      size="small"
                      sx={{
                        ...typeChipSx,
                        bgcolor: lifecycleChip.bg,
                        color: lifecycleChip.color,
                        border: `1px solid ${lifecycleChip.border}`,
                      }}
                    />
                  )}
                </Stack>
              </Stack>

              {title && (
                <Typography
                  sx={{
                    fontSize: {
                      xs: "0.98rem",
                      sm: "1.08rem",
                    },
                    fontWeight: 800,
                    color: TEXT.primary,
                    lineHeight: 1.3,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {title}
                </Typography>
              )}

              {isImpactCard ? (
                <ImpactCardFundsReport
                  cardId={mainCardId}
                  talentId={talentId}
                  purchaseId={purchase?.id}
                  cardTitle={title}
                  impactGoal={impactFunds?.impactGoal}
                  expectedOutcome={impactFunds?.expectedOutcome}
                  targetAmount={impactFunds?.targetAmount}
                  committedFund={impactFunds?.committedFund}
                  verifiedFund={impactFunds?.verifiedFund}
                  contributorCount={impactFunds?.contributorCount}
                  collectorCount={impactFunds?.collectorCount}
                  clickedCount={impactFunds?.clickedCount}
                  viewedCount={impactFunds?.viewedCount}
                  useOfFunds={impactFunds?.useOfFunds}
                  beneficiaryName={impactFunds?.beneficiaryName}
                  isOwner={isOwner}
                  isLoggedIn={isLoggedIn}
                  isCollected={purchased}
                  onRequireLogin={onRequireLogin}
                />
              ) : null}

              {/* Chips + collected date */}
              {!isImpactCard && (
              <Stack
                spacing={0.85}
                pt={0.25}
                sx={{
                  minWidth: 0,
                  width: "100%",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems={{
                    xs: "flex-start",
                    sm: "center",
                  }}
                  flexWrap="wrap"
                  useFlexGap
                  rowGap={0.5}
                  columnGap={1}
                  sx={{
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  {Number.isFinite(chipPrice) && chipPrice >= 0 && !isCollection && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 0.5,
                        minWidth: 0,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: {
                            xs: "1.05rem",
                            sm: "1.15rem",
                          },
                          fontWeight: 800,
                          lineHeight: 1,
                          color: TEXT.stat,
                        }}
                      >
                        {chipPrice}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: TEXT.primary,
                          textTransform: "capitalize",
                        }}
                      >
                        {t("deck_chips")}
                      </Typography>
                    </Box>
                  )}

                  {purchased && (
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{
                        minWidth: 0,
                        flexShrink: 1,
                        maxWidth: "100%",
                      }}
                    >
                      <CheckIcon
                        sx={{
                          fontSize: 14,
                          color: TEXT.stat,
                          flexShrink: 0,
                        }}
                      />

                      <Typography
                        sx={{
                          fontSize: "0.62rem",
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          color: TEXT.stat,
                          textTransform: "uppercase",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("deck_collected")}
                      </Typography>

                      {purchasedDateLabel ? (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 700,
                            letterSpacing: "0.04em",
                            color: TEXT.primary,
                            textTransform: "uppercase",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {purchasedDateLabel}
                        </Typography>
                      ) : null}
                    </Stack>
                  )}
                </Stack>

                {/* Collection status pills */}
                {!isCollection && (
                  <Stack
                    direction="row"
                    spacing={0.6}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    {purchased ? (
                      <>
                        <Chip
                          icon={
                            <RadioButtonUncheckedRoundedIcon
                              sx={{
                                fontSize: 14,
                                color: "var(--secondaryBorder)",
                                "& path": {
                                  stroke: "var(--secondaryBorder)",
                                  strokeWidth: 1,
                                },
                                backgroundColor: "var(--secondaryBorderAlpha)",
                                borderRadius: "5px",
                              }}
                            />
                          }
                          label={t("deck_collected")}
                          size="small"
                          sx={{
                            ...statusPillBaseSx,
                            bgcolor: "transparent",
                            color: "var(--secondaryBorder)",
                            borderRadius: "5px",
                            border: "1px solid var(--secondaryBorder)",
                            "& .MuiChip-label": {
                              pt: 0.2,
                            },
                          }}
                        />

                        {alreadySaved && (
                          <Chip
                            icon={
                              <StarRoundedIcon
                                sx={{
                                  fontSize: 14,
                                  "& path": {
                                    fill: GOLD_LIGHT,
                                  },
                                }}
                              />
                            }
                            label={t("deck_saved_to_charm")}
                            size="small"
                            sx={{
                              ...statusPillBaseSx,
                              bgcolor: "rgba(200, 164, 75, 0.1)",
                              color: TEXT.label,
                              borderRadius: "5px",
                              border: `1px solid ${GOLD_DARK}`,
                              "& .MuiChip-label": {
                                pt: 0.2,
                              },
                            }}
                          />
                        )}
                      </>
                    ) : (
                      <Chip
                        icon={
                          <RadioButtonUncheckedRoundedIcon
                            sx={{
                              fontSize: 14,
                              color: GOLD_DARK,
                              "& path": {
                                stroke: GOLD_DARK,
                                strokeWidth: 1,
                              },
                            }}
                          />
                        }
                        label={t("deck_not_collected")}
                        size="small"
                        sx={{
                          ...statusPillBaseSx,
                          bgcolor: "transparent",
                          color: TEXT.muted,
                          borderRadius: "5px",
                          border: `1px solid ${GOLD_DARK}`,
                          "& .MuiChip-label": {
                            pt: 0.2,
                          },
                        }}
                      />
                    )}
                  </Stack>
                )}

                {isSealing && countdownLabel && (
                  <Stack
                    direction="row"
                    spacing={{ xs: 0.7, sm: 1 }}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                    sx={{
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: TEXT.muted,
                      }}
                    >
                      {t("deck_active_until")}
                    </Typography>

                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.65,
                        px: {
                          xs: 1,
                          sm: 1.2,
                        },
                        py: 0.55,
                        borderRadius: "999px",
                        border: `1px solid ${GOLD_DARK}`,
                        background:
                          "linear-gradient(114deg, rgba(200,164,75,0.28), rgba(200,164,75,0.1))",
                        flexShrink: 1,
                        maxWidth: "100%",
                        minWidth: 0,
                      }}
                    >
                      <ScheduleRoundedIcon
                        sx={{
                          fontSize: 14,
                          color: TEXT.label,
                          flexShrink: 0,
                        }}
                      />

                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          fontSize: {
                            xs: "0.7rem",
                            sm: "0.78rem",
                          },
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                          lineHeight: 1,
                          color: TEXT.primary,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {countdownLabel}
                      </Typography>
                    </Box>
                  </Stack>
                )}
              </Stack>
              )}
            </Stack>
          </Box>
        </Box>

        {/* Content Added */}
        {!isCollection && !isImpactCard && (
          <Box sx={surfaceSx}>
            <Stack spacing={0.55}>
              <Typography
                sx={{
                  fontSize: "0.86rem",
                  fontWeight: 700,
                  color: TEXT.primary,
                }}
              >
                {t("deck_content_added")}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.82rem",
                  minWidth: 0,
                  overflowWrap: "break-word",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: TEXT.primary,
                    fontSize: {
                      xs: "15px",
                      sm: "16px",
                    },
                  }}
                >
                  {pieceCount}
                </Box>

                <Box
                  component="span"
                  sx={{
                    color: TEXT.muted,
                  }}
                >
                  {" "}
                  {t("deck_pieces_added")}
                </Box>
              </Typography>

              {hasSealTime && (
                <Typography
                  sx={{
                    fontSize: "0.78rem",
                    color: TEXT.muted,
                    overflowWrap: "break-word",
                  }}
                >
                  {isSealed ? t("deck_sealed_on") : t("deck_seals_on")}{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: TEXT.primary,
                      fontSize: "14px",
                    }}
                  >
                    {sealDateLabel}
                  </Box>
                </Typography>
              )}
            </Stack>
          </Box>
        )}

        {/* Actions */}
        <Box
          sx={{
            minWidth: 0,
            width: "100%",
          }}
        >
          <Typography sx={sectionLabelSx}>{t("deck_details_actions")}</Typography>

          <Box sx={surfaceSx}>
            {purchased ? (
              <Stack
                direction="row"
                spacing={{
                  xs: 0.75,
                  sm: 1,
                }}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
                sx={{
                  width: "100%",
                  minWidth: 0,
                }}
              >
                {alreadySaved ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleOpenRemoveDialog}
                    sx={actionBtnSx}
                  >
                    {t("remove_from_charm")}
                  </Button>
                ) : (
                  <SaveCard
                    mainCardId={mainCardId}
                    deckStatus={deckStatus}
                    buttonSx={actionBtnSx}
                  />
                )}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <MenuBookOutlinedIcon
                      sx={{
                        fontSize: "1rem !important",
                      }}
                    />
                  }
                  onClick={onOpenDetails}
                  sx={secondaryBtnSx}
                >
                  {t("deck_open_full_card")}
                </Button>
              </Stack>
            ) : (
              <Stack
                direction="row"
                spacing={{
                  xs: 0.75,
                  sm: 1,
                }}
                flexWrap="wrap"
                useFlexGap
                alignItems="center"
                sx={{
                  width: "100%",
                  minWidth: 0,
                }}
              >
                {canCollectImpact && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => void handleCollectImpact()}
                    disabled={isCollectingImpact || (isLoggedIn && !onCollectImpact)}
                    sx={actionBtnSx}
                  >
                    {isCollectingImpact ? "Collecting..." : "Collect for free"}
                  </Button>
                )}

                {canBuy && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleBuyClick}
                    sx={actionBtnSx}
                  >
                    {t("collect_card")}
                  </Button>
                )}

                {alreadySaved ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleOpenRemoveDialog}
                    sx={actionBtnSx}
                  >
                    {t("remove_from_charm")}
                  </Button>
                ) : (
                  <SaveCard
                    mainCardId={mainCardId}
                    deckStatus={deckStatus}
                    buttonSx={actionBtnSx}
                  />
                )}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <MenuBookOutlinedIcon
                      sx={{
                        fontSize: "1rem !important",
                      }}
                    />
                  }
                  onClick={onOpenDetails}
                  sx={secondaryBtnSx}
                >
                  {t("deck_open_full_card")}
                </Button>
              </Stack>
            )}
          </Box>
        </Box>
      </Stack>

      {canBuy && (
        <Dialog
          open={purchaseOpen}
          onClose={() => !isPurchasing && setPurchaseOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: "#16120e",
              border: `1px solid ${GOLD_DARK}`,
              mx: {
                xs: 1.5,
                sm: 2,
              },
              width: {
                xs: "calc(100% - 24px)",
                sm: "100%",
              },
              maxWidth: "444px",
              boxSizing: "border-box",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: TEXT.primary,
              fontWeight: 800,
              fontSize: "1.05rem",
              overflowWrap: "break-word",
            }}
          >
            {details.confirmTitle}
          </DialogTitle>

          <DialogContent>
            <Typography
              sx={{
                fontSize: "0.88rem",
                color: TEXT.muted,
                lineHeight: 1.55,
                overflowWrap: "break-word",
              }}
            >
              {details.confirmBody(chipPrice)}
            </Typography>
          </DialogContent>

          <DialogActions
            sx={{
              px: {
                xs: 1.75,
                sm: 2.5,
              },
              pb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={() => setPurchaseOpen(false)}
              disabled={isPurchasing}
              sx={{
                textTransform: "none",
                color: TEXT.muted,
              }}
            >
              {t("cancel")}
            </Button>

            <Button
              variant="contained"
              onClick={() => void handleConfirmChipPurchase()}
              disabled={isPurchasing}
              sx={actionBtnSx}
            >
              {isPurchasing ? t("processing") : t("collect_card")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={openRemoveDialog}
        onClose={handleCloseRemoveDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "var(--primary-100-light)",
            backgroundImage: "none",
            border: "1px solid var(--primary-300)",
            boxShadow: "var(--shadow-md)",
            m: { xs: 2, sm: 3 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: TAAS_SANS,
            fontWeight: 700,
            fontSize: { xs: "1.05rem", sm: "1.25rem" },
            color: "var(--primary-900)",
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 2.5 },
          }}
        >
          {t("remove_from_charm")}
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <DialogContentText
            sx={{
              color: "var(--primary-800)",
              fontFamily: TAAS_SANS,
              fontSize: { xs: "0.875rem", sm: "0.95rem" },
              lineHeight: 1.55,
            }}
          >
            {t("remove_from_charm_confirmation")}
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2.5 }, gap: 1 }}>
          <Button
            onClick={handleCloseRemoveDialog}
            sx={{
              textTransform: "none",
              color: "var(--primary-800)",
              fontFamily: TAAS_SANS,
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
            }}
          >
            {t("cancel")}
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={() => void handleRemoveCardFromCharmCards()}
            sx={{
              textTransform: "none",
              fontFamily: TAAS_SANS,
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
            }}
          >
            {t("remove")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeckCardExpandDetailsDrawer;
